import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Share2, Heart, Grid3X3, Sparkles, UserPlus, UserCheck, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useFollow } from '@/hooks/useFollow';
import { toast } from '@/hooks/use-toast';
import { MessagingModal } from '@/components/messaging/MessagingModal';
import auraLogo from '@/assets/aura-logo.png';

// Public profile data from the public_profiles view (limited fields for privacy)
interface PublicProfileData {
  id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  aura_level: string | null;
  aura_points: number;
  // These fields are NOT in public_profiles view, so we make them optional
  style_personality?: string | null;
  preferred_colors?: string[] | null;
  is_premium?: boolean;
}

interface FeedPost {
  id: string;
  outfit_data: {
    items: Array<{ image_url: string | null; name: string }>;
  };
  likes_count: number;
  created_at: string;
}

export default function PublicProfile() {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();
  const { followUser, unfollowUser, isFollowing } = useFollow();
  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [showMessaging, setShowMessaging] = useState(false);
  const [isMutual, setIsMutual] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!username) return;

      // Use public_profiles view for viewing other users' profiles
      const { data: profileData, error } = await supabase
        .from('public_profiles' as any)
        .select('*')
        .eq('username', username.toLowerCase())
        .single() as { data: PublicProfileData | null; error: any };

      if (error || !profileData) {
        setLoading(false);
        return;
      }

      setProfile(profileData);

      // Fetch posts
      const { data: postsData } = await supabase
        .from('feed_posts')
        .select('*')
        .eq('user_id', profileData.id)
        .order('created_at', { ascending: false })
        .limit(12);

      if (postsData) {
        setPosts(postsData.map(p => ({
          ...p,
          outfit_data: p.outfit_data as FeedPost['outfit_data']
        })));
      }

      // Fetch follower counts
      const { count: followers } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', profileData.id);

      const { count: following } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('follower_id', profileData.id);

      setFollowerCount(followers || 0);
      setFollowingCount(following || 0);

      // Check if mutual follow
      if (user && user.id !== profileData.id) {
        const { data: theyFollowMe } = await supabase
          .from('user_follows')
          .select('id')
          .eq('follower_id', profileData.id)
          .eq('following_id', user.id)
          .single();

        const { data: iFollowThem } = await supabase
          .from('user_follows')
          .select('id')
          .eq('follower_id', user.id)
          .eq('following_id', profileData.id)
          .single();

        setIsMutual(!!theyFollowMe && !!iFollowThem);
      }

      setLoading(false);
    };

    fetchProfile();
  }, [username, user]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile?.display_name || 'Stylist'} on AURA`,
          text: `Check out ${profile?.display_name}'s style profile!`,
          url
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied!" });
    }
  };

  const handleFollowToggle = async () => {
    if (!profile || !user) return;
    
    if (isFollowing(profile.id)) {
      await unfollowUser(profile.id);
    } else {
      await followUser(profile.id);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="text-6xl mb-4">😢</div>
        <h1 className="text-xl font-bold text-foreground mb-2">Profile not found</h1>
        <p className="text-muted-foreground mb-6">This user doesn't exist or hasn't set up their profile yet.</p>
        <Link to="/">
          <Button>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        </Link>
      </div>
    );
  }

  const isOwnProfile = user?.id === profile.id;

  return (
    <>
      <Helmet>
        <title>{profile.display_name || 'Stylist'} - AURA Style Profile</title>
        <meta name="description" content={`Check out ${profile.display_name}'s style profile on AURA. ${profile.style_personality || 'Fashion enthusiast'}`} />
        <meta property="og:title" content={`${profile.display_name || 'Stylist'} on AURA`} />
        <meta property="og:description" content={`${profile.style_personality || 'Fashion enthusiast'} | ${profile.aura_level}`} />
        <meta property="og:image" content={profile.avatar_url || auraLogo} />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-lg border-b border-border">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <img src={auraLogo} alt="AURA" className="h-8" />
            <Button variant="ghost" size="icon" onClick={handleShare}>
              <Share2 className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Profile Section */}
        <div className="max-w-lg mx-auto px-4 py-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            {/* Avatar */}
            <div className="relative inline-block mb-4">
              <Avatar className="w-28 h-28 border-4 border-primary/20">
                <AvatarImage src={profile.avatar_url || undefined} />
                <AvatarFallback className="bg-primary/20 text-primary text-3xl">
                  {profile.display_name?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              {profile.is_premium && (
                <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* Name & Username */}
            <h1 className="text-2xl font-bold text-foreground mb-1">
              {profile.display_name || 'Stylist'}
            </h1>
            {profile.username && (
              <p className="text-muted-foreground mb-2">@{profile.username}</p>
            )}

            {/* Badges */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <Badge variant="secondary" className="bg-primary/10 text-primary">
                {profile.aura_level}
              </Badge>
              {profile.style_personality && (
                <Badge variant="outline">{profile.style_personality}</Badge>
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center justify-center gap-8 mb-6">
              <div className="text-center">
                <p className="text-xl font-bold text-foreground">{posts.length}</p>
                <p className="text-xs text-muted-foreground">Posts</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-foreground">{followerCount}</p>
                <p className="text-xs text-muted-foreground">Followers</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-foreground">{followingCount}</p>
                <p className="text-xs text-muted-foreground">Following</p>
              </div>
            </div>

            {/* Actions */}
            {!isOwnProfile && user && (
              <div className="flex items-center justify-center gap-3">
                <Button
                  onClick={handleFollowToggle}
                  variant={isFollowing(profile.id) ? "outline" : "default"}
                  className="gap-2"
                >
                  {isFollowing(profile.id) ? (
                    <>
                      <UserCheck className="w-4 h-4" />
                      Following
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      Follow
                    </>
                  )}
                </Button>
                {isMutual && (
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setShowMessaging(true)}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}

            {/* Preferred Colors */}
            {profile.preferred_colors && profile.preferred_colors.length > 0 && (
              <div className="mt-6">
                <p className="text-sm text-muted-foreground mb-2">Favorite Colors</p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {profile.preferred_colors.map((color, idx) => (
                    <span
                      key={idx}
                      className="w-6 h-6 rounded-full border-2 border-border shadow-sm"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Posts Grid */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-center gap-2 mb-4 text-muted-foreground">
              <Grid3X3 className="w-4 h-4" />
              <span className="text-sm font-medium">Posts</span>
            </div>

            {posts.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No posts yet</p>
              </Card>
            ) : (
              <div className="grid grid-cols-3 gap-1">
                {posts.map((post, idx) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="aspect-square bg-muted relative overflow-hidden"
                  >
                    {post.outfit_data.items[0]?.image_url ? (
                      <img
                        src={post.outfit_data.items[0].image_url}
                        alt="Outfit"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">
                        👔
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                      <div className="flex items-center gap-1 text-white">
                        <Heart className="w-4 h-4 fill-current" />
                        <span className="text-sm font-medium">{post.likes_count}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* CTA for non-users */}
          {!user && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-8"
            >
              <Card className="p-6 text-center bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <Sparkles className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-bold text-foreground mb-2">Join AURA</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your own style profile and connect with fashion enthusiasts worldwide.
                </p>
                <Link to="/auth">
                  <Button className="w-full">Get Started</Button>
                </Link>
              </Card>
            </motion.div>
          )}
        </div>
      </div>

      {/* Messaging Modal */}
      <MessagingModal
        isOpen={showMessaging}
        onClose={() => setShowMessaging(false)}
        initialUserId={profile.id}
        initialUserName={profile.display_name || undefined}
      />
    </>
  );
}
